import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/redux/hooks'
import { toast } from 'sonner'
import { GameRoomStatus } from '@/redux/types/room'
import {
  fetchRooms,
  getRoomDetails,
  resetGame,
  updateRoomDetails,
} from '@/redux/asyncThunks/room'
import {
  endGame as endGameThunk,
  fetchTimerConfigurationThunk,
  startNextRoundThunk,
} from '@/redux/asyncThunks/timerConfiguration'
import {
  connectTimerWebSocket,
  disconnectTimerWebSocket,
} from '@/redux/asyncThunks/timerWebsocket'
import { getAccessToken } from '@/utils/authUtils'

interface GameControlsState {
  isStarting: boolean
  isEnding: boolean
  isResetting: boolean
}

interface UseGameControlsProps {
  roomId: string
  scenarioTitle?: string
  isGameReadyToStart?: boolean
  status: GameRoomStatus
  isSimulating?: boolean
}

export const useGameControls = ({
  roomId,
  scenarioTitle,
  isGameReadyToStart,
  status,
  isSimulating,
}: UseGameControlsProps) => {
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState<GameControlsState>({
    isStarting: false,
    isEnding: false,
    isResetting: false,
  })

  const withTimerConnection = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T> => {
      const jwtToken = getAccessToken()
      if (!jwtToken) {
        throw new Error('No authentication token available')
      }

      try {
        await dispatch(connectTimerWebSocket({ jwtToken, roomId })).unwrap()

        const result = await operation()

        await new Promise((resolve) => setTimeout(resolve, 200))

        return result
      } finally {
        try {
          await dispatch(disconnectTimerWebSocket()).unwrap()
        } catch (error) {
          console.error('Failed to disconnect timer websocket:', error)
        }
      }
    },
    [dispatch, roomId]
  )

  const canStartGame = useCallback(() => {
    return (
      isGameReadyToStart &&
      status !== GameRoomStatus.IN_PROGRESS &&
      status !== GameRoomStatus.COMPLETED
    )
  }, [isGameReadyToStart, status])

  const canEndGame = useCallback(() => {
    return status === GameRoomStatus.IN_PROGRESS
  }, [status])

  const canResetGame = useCallback(() => {
    if (isSimulating) {
      return false
    }
    return status !== GameRoomStatus.NOT_STARTED
  }, [status, isSimulating])

  const startGame = useCallback(async () => {
    if (!canStartGame()) {
      toast.error('Game cannot be started at this time')
      return false
    }

    setLoading((prev) => ({ ...prev, isStarting: true }))

    try {
      await withTimerConnection(async () => {
        await dispatch(getRoomDetails(roomId)).unwrap()

        await dispatch(
          updateRoomDetails({
            roomId: roomId,
            scenario_title: scenarioTitle,
            status: GameRoomStatus.IN_PROGRESS,
            start_time: new Date(),
          })
        ).unwrap()

        await dispatch(fetchTimerConfigurationThunk(roomId)).unwrap()
        await dispatch(startNextRoundThunk(roomId)).unwrap()
      })

      await dispatch(fetchRooms()).unwrap()

      toast.success('Game started successfully')
      return true
    } catch (error) {
      console.error('Failed to start game:', error)
      toast.error('Failed to start game. Please try again.')
      return false
    } finally {
      setLoading((prev) => ({ ...prev, isStarting: false }))
    }
  }, [dispatch, roomId, scenarioTitle, canStartGame, withTimerConnection])

  const endGame = useCallback(async () => {
    if (!canEndGame()) {
      toast.error('Game cannot be ended at this time')
      return false
    }

    setLoading((prev) => ({ ...prev, isEnding: true }))

    try {
      await withTimerConnection(async () => {
        await dispatch(endGameThunk()).unwrap()
      })

      await dispatch(fetchRooms()).unwrap()

      toast.success('Game ended successfully')
      return true
    } catch (error) {
      console.error('Failed to end game:', error)
      toast.error('Failed to end game. Please try again.')
      return false
    } finally {
      setLoading((prev) => ({ ...prev, isEnding: false }))
    }
  }, [dispatch, canEndGame, withTimerConnection])

  const handleResetGame = useCallback(async () => {
    if (!canResetGame()) {
      toast.error('Game cannot be reset at this time')
      return false
    }

    setLoading((prev) => ({ ...prev, isResetting: true }))

    try {
      await dispatch(getRoomDetails(roomId)).unwrap()
      await dispatch(resetGame(roomId)).unwrap()
      await dispatch(fetchRooms()).unwrap()

      toast.success('Game reset successfully')
      return true
    } catch (error) {
      console.error('Failed to reset game:', error)
      toast.error('Failed to reset game. Please try again.')
      return false
    } finally {
      setLoading((prev) => ({ ...prev, isResetting: false }))
    }
  }, [dispatch, roomId, canResetGame])

  return {
    loading,
    canStartGame: canStartGame(),
    canEndGame: canEndGame(),
    canResetGame: canResetGame(),
    isSimulating,
    startGame,
    endGame,
    handleResetGame,
  }
}
